import { Table } from "@tanstack/react-table";
import { useMemo } from "react";

export interface DataTablePaginationConfig {
  serverSidePagination: boolean;
  pageSize: number;
  table: Table<any>;
  serverTotalRows?: number;
  serverCurrentPage?: number;
  serverTotalPages?: number;
}

export interface DataTablePaginationState {
  currentPage: number;
  totalPages: number;
  totalRows: number;
  /**
   * The page size actually in effect. For client-side tables that is TanStack's
   * live value, which diverges from the `pageSize` prop as soon as the user picks
   * a different size in the size changer — the prop keeps the configured default.
   * Consumers must render this rather than the prop, or they report a range and a
   * size that do not match the rows on screen.
   */
  pageSize: number;
}

/**
 * Hook to manage DataTable pagination state and calculations
 */
export function useDataTablePagination(
  config: DataTablePaginationConfig
): DataTablePaginationState {
  const {
    serverSidePagination,
    pageSize,
    table,
    serverTotalRows,
    serverCurrentPage,
    serverTotalPages,
  } = config;

  const hasClientFilter = table.getState().globalFilter;
  const filteredRows = table.getFilteredRowModel().rows.length;
  const clientPageIndex = table.getState().pagination.pageIndex;
  const clientPageSize = table.getState().pagination.pageSize;

  return useMemo(() => {
    const totalRows =
      serverSidePagination && !hasClientFilter
        ? serverTotalRows || 0
        : filteredRows;

    const currentPage =
      serverSidePagination && !hasClientFilter
        ? serverCurrentPage || 1
        : clientPageIndex + 1;

    const effectivePageSize =
      serverSidePagination && !hasClientFilter ? pageSize : clientPageSize;

    const totalPages =
      serverSidePagination && !hasClientFilter
        ? serverTotalPages || 0
        : Math.ceil(filteredRows / effectivePageSize);

    return { currentPage, totalPages, totalRows, pageSize: effectivePageSize };
  }, [
    serverSidePagination,
    serverTotalRows,
    serverCurrentPage,
    serverTotalPages,
    pageSize,
    hasClientFilter,
    filteredRows,
    clientPageIndex,
    // Without this the memo kept a stale page size after the user changed it on a
    // client-side table, so `totalPages` (and now the row range) lagged one change
    // behind the rows actually rendered.
    clientPageSize,
  ]);
}
